<template>
    <vx-table-row>
        <vx-table-row-title>
            <div class="flex">
                <vx-badge class="mr-2 cursor-pointer" @click.prevent="copyId()" size="xs" color="slate">{{
                    props.modelValue.id
                }}</vx-badge>
                <div class="line-clamp-none break-all lg:line-clamp-2">
                    {{ props.modelValue.name }}
                </div>
            </div>
        </vx-table-row-title>

        <vx-table-row-item>
            <template v-slot:label> Event: </template>

            <vx-popover v-if="trigger" hover :arrow="true" placement="top">
                <vx-badge size="sm" :color="trigger.color">
                    <font-awesome-icon widthAuto :icon="trigger.icon"></font-awesome-icon>
                    {{ trigger.short_label || trigger.label }}
                </vx-badge>
                <template v-if="trigger" v-slot:content>
                    <span class="font-mono">{{ trigger.value }}</span>
                </template>
            </vx-popover>
            <template v-else>
                <vx-icon-loading spin></vx-icon-loading>
            </template>
        </vx-table-row-item>
        <vx-table-row-item contentClasses="max-w-full overflow-hidden">
            <template v-slot:label> Filters: </template>

            {{ props.modelValue.filters_count }}
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Actions: </template>

            <vx-popover hover :arrow="true" placement="top">
                {{ props.modelValue.live_actions_count }} / {{ props.modelValue.actions_count }}
                <template v-slot:content>
                    Automations: {{ props.modelValue.live_actions_count }} active / {{ props.modelValue.actions_count }}
                    total
                </template>
            </vx-popover>
        </vx-table-row-item>
        <vx-table-row-item>
            <template v-slot:label> Updated: </template>

            <vx-popover hover :arrow="true" placement="top" :key="updatedAtKey">
                {{ fromNow(props.modelValue.updated_at) }}
                <template v-slot:content>
                    {{ shortDateTime(props.modelValue.updated_at) }}
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-item v-if="FEATURES.flows">
            <template v-slot:label> Flow: </template>
            <vx-popover
                v-if="props.modelValue.flow_id"
                hover
                :arrow="true"
                placement="top"
                @mouseenter="loadFlowOnHover"
                data-test="flow-popover"
            >
                <font-awesome-icon widthAuto :icon="faSlidersSimple"></font-awesome-icon>
                <template v-slot:content>
                    <span v-if="flow" class="text-sm font-normal">{{ flow.name }}</span>
                    <font-awesome-icon v-else widthAuto :icon="faLoader" spin></font-awesome-icon>
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-buttons>
            <vx-dropdown placement="right">
                <vx-button data-test="rule-dropdown" type="button" color="muted" outline class="w-[45px]">
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>

                <template v-slot:items>
                    <vx-dropdown-item :href="`/automation/event/new?duplicate_from_id=${props.modelValue.id}`">
                        Duplicate
                        <font-awesome-icon widthAuto :icon="faCopy"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item data-test="delete" @click="deleteAutomationRule()">
                        Delete
                        <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item data-test="turn-all-actions-on" @click="updateActionsStatus('published')">
                        All Actions - On
                        <font-awesome-icon widthAuto :icon="faToggleLargeOn"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item data-test="turn-all-actions-off" @click="updateActionsStatus('draft')">
                        All Actions - Off
                        <font-awesome-icon widthAuto :icon="faToggleLargeOff"></font-awesome-icon>
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>

            <vx-button :href="`/automation/event/${props.modelValue.id}`" color="muted" outline>
                Edit <font-awesome-icon widthAuto :icon="faFilePen" />
            </vx-button>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script setup>
import {
    faCopy,
    faEllipsis,
    faFilePen,
    faLoader,
    faToggleLargeOff,
    faToggleLargeOn,
    faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { faSlidersSimple } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxIconLoading,
    VxPopover,
    VxSwal,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { useInterval } from '@vueuse/core';
import { computed, inject } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { automationRulesClient } from '~/services';
import { fromNow, shortDateTime } from '~/utils/date';
import { useAutomationRulesMetadata } from './useAutomationRulesMetadata';
import { throwSwalRequestError } from '~/utils/request';
import { useResourcesFlow } from '~/composables/useResourcesFlow';

const FEATURES = inject('FEATURES');
const toasted = useToasted();

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
});

const { triggers } = useAutomationRulesMetadata();

const emit = defineEmits(['deleted', 'update:modelValue']);

const { flows, loadFlow } = useResourcesFlow();
const flow = computed(() =>
    FEATURES.flows && props.modelValue.flow_id && flows.value.has(props.modelValue.flow_id)
        ? flows.value.get(props.modelValue.flow_id)
        : undefined
);

const trigger = computed(() => {
    return triggers.value.find((trigger) => trigger.value === props.modelValue.event);
});

const deleteAutomationRule = async () => {
    VxSwal.confirmation('Delete Automation', 'Are you sure? There is no undo.', 'danger', () =>
        automationRulesClient
            .delete(props.modelValue.team_id, props.modelValue.id)
            .then(() => emit('deleted', props.modelValue.id))
            .catch(throwSwalRequestError)
    );
};

const updateActionsStatus = (status) => {
    VxSwal.confirmation(
        'Update Actions Status',
        `Are you sure you want to turn ${status === 'published' ? 'on' : 'off'} all actions?`,
        'success',
        () =>
            automationRulesClient
                .updateActionsStatus(props.modelValue.team_id, props.modelValue.id, status)
                .then(() => {
                    emit('update:modelValue', {
                        ...props.modelValue,
                        live_actions_count: status === 'published' ? props.modelValue.actions_count : 0,
                    });
                })
                .catch(throwSwalRequestError)
    );
};

const updatedAtKey = useInterval(60000);

const copyId = () => {
    navigator.clipboard.writeText(props.modelValue.id);
    toasted.success(`${props.modelValue.id} copied to clipboard.`);
};

const loadFlowOnHover = () => {
    if (flow.value === undefined) {
        loadFlow(props.modelValue.team_id, props.modelValue.flow_id);
    }
};
</script>
